/**
 * ================================================================
 * USE SESSION HOOK - TanStack Query for Session State
 * ================================================================
 */

import { useQuery } from "@tanstack/react-query";
import {
  betterAuthClient as authClient,
  type Session,
  type User,
} from "../services";
import { sessionService } from "../services";
import { profileService } from "@/features/common/services/profile.service";
import { queryKeys } from "@/lib/queryClient";

export interface SessionData {
  session: Session | null;
  user: User | null;
  isAuthenticated: boolean;
}

/**
 * Hook to get current session state
 * Automatically syncs with Zustand store
 */
export function useSession() {
  return useQuery({
    queryKey: queryKeys.auth.session(),
    /**
     * AUTH CONTRACT RULE #2 + #4
     * - Native auth state is token-first.
     * - Better Auth session remains fallback metadata for hydration.
     */
    queryFn: async (): Promise<SessionData> => {
      const accessToken = await sessionService.getAccessToken();
      const { data } = await authClient.getSession();
      const session = data?.session || null;
      const sessionUser = data?.user || null;
      let fallbackUser: User | null = null;

      if (!sessionUser && accessToken) {
        try {
          const profile = await profileService.getCurrentUserProfile();
          fallbackUser = {
            id: profile.id,
            email: profile.email,
            phone: profile.phoneNumber,
            fullName: profile.fullName,
            avatarUrl: profile.avatarUrl,
          };
        } catch {
          fallbackUser = null;
        }
      }

      const user = (sessionUser as User | null) || fallbackUser;

      // Store managed session now, betterAuth manages its own token.

      return {
        session: session as Session | null, // Type assertion might be needed if mismatch
        user,
        isAuthenticated: !!accessToken || (!!session && !!user),
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    // Enable only if we think we might be authenticated
    // BetterAuth check uses cookies/token automatically
  });
}

/**
 * AUTH CONTRACT RULE #3
 * Use this hook (or authService.getAuthenticatedUser) as the only identity read-path
 * in feature code to prevent token/session drift.
 */
export function useCurrentUser() {
  const { data, isLoading, error } = useSession();

  return {
    user: data?.user || null,
    isLoading,
    error,
    isAuthenticated: data?.isAuthenticated || false,
  };
}
